import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { FundRawRecord } from '@/components/funds-raw/types';
import FundsAddToPortfolioDialog from '@/components/funds-raw/AddToPortfolioDialog';

// Minimal payload union for initial phase (funds_raw only). Extend in later phases if needed.
export type AddToPortfolioPayload =
  | {
      type: 'funds_raw';
      fund?: FundRawRecord | null;
      funds?: FundRawRecord[];
      onSuccess?: () => void;
    };

interface AddToPortfolioContextValue {
  open: (payload: AddToPortfolioPayload) => void;
  close: () => void;
}

const AddToPortfolioContext = createContext<AddToPortfolioContextValue | undefined>(undefined);

export function AddToPortfolioProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [payload, setPayload] = useState<AddToPortfolioPayload | null>(null);

  const open = useCallback((p: AddToPortfolioPayload) => {
    setPayload(p);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    // Allow the dialog to close gracefully before clearing payload
    setTimeout(() => setPayload(null), 0);
  }, []);

  const value = useMemo(() => ({ open, close }), [open, close]);

  return (
    <AddToPortfolioContext.Provider value={value}>
      {children}
      {/* Single dialog instance, rendered conditionally based on payload type */}
      {payload?.type === 'funds_raw' && (
        <FundsAddToPortfolioDialog
          isOpen={isOpen}
          onClose={close}
          fund={payload.fund ?? null}
          funds={payload.funds}
          onSuccess={payload.onSuccess}
        />
      )}
    </AddToPortfolioContext.Provider>
  );
}

export function useAddToPortfolioContext() {
  const ctx = useContext(AddToPortfolioContext);
  if (!ctx) throw new Error('useAddToPortfolioContext must be used within AddToPortfolioProvider');
  return ctx;
}
